import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Navbar } from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Package, Heart, Bell, User, ArrowRight, ArrowLeft, Clock, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const { t, isRTL } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersRes, notifRes] = await Promise.all([
          axios.get(`${API}/orders`, { headers: getAuthHeaders() }),
          axios.get(`${API}/notifications`, { headers: getAuthHeaders() }),
        ]);
        setOrders(ordersRes.data.slice(0, 5));
        setNotifications(notifRes.data.slice(0, 5));
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      out_for_delivery: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t("pending"),
      processing: t("processing"),
      out_for_delivery: t("outForDelivery"),
      delivered: t("delivered"),
      cancelled: t("cancelled"),
    };
    return labels[status] || status;
  };

  const stats = [
    { label: t("totalOrders"), value: orders.length, icon: Package, color: "bg-blue-500" },
    { label: t("pending"), value: orders.filter((o) => o.status === "pending").length, icon: Clock, color: "bg-yellow-500" },
    { label: t("delivered"), value: orders.filter((o) => o.status === "delivered").length, icon: CheckCircle, color: "bg-green-500" },
    { label: t("notifications"), value: notifications.filter((n) => !n.is_read).length, icon: Bell, color: "bg-red-500" },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8" data-testid="customer-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">
            {t("welcome")}، {user?.full_name || user?.username}!
          </h1>
          <p className="text-muted-foreground">{t("manageOrders")}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {stats.map((stat) => (
            <Card key={stat.label} className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Orders */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">{t("recentOrders")}</CardTitle>
                <Link to="/orders">
                  <Button variant="ghost" size="sm">
                    {t("viewAll")} <ArrowIcon className={`${isRTL ? 'mr-2' : 'ml-2'} h-4 w-4`} />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-20 bg-muted animate-pulse rounded" />
                    ))}
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{t("noOrdersYet")}</p>
                    <Link to="/products">
                      <Button className="mt-4 bg-[#1a365d]">{t("startShopping")}</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div
                        key={order.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{t("order")} #{order.id.slice(0, 8)}</p>
                          <p className="text-sm text-muted-foreground">
                            {order.items?.length || 0} {t("items")} • {order.total} {t("lyd")}
                          </p>
                        </div>
                        <Badge className={getStatusColor(order.status)}>
                          {getStatusLabel(order.status)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions & Notifications */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading">{t("quickActions")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/products" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Package className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                    {t("browseProductsBtn")}
                  </Button>
                </Link>
                <Link to="/favorites" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Heart className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                    {t("myFavoritesBtn")}
                  </Button>
                </Link>
                <Link to="/profile" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <User className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                    {t("editProfileBtn")}
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Notifications */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  {t("notifications")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t("noNotifications")}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className={`p-3 rounded-lg ${notif.is_read ? "bg-slate-50" : "bg-blue-50"}`}
                      >
                        <p className="font-medium text-sm">{notif.title}</p>
                        <p className="text-xs text-muted-foreground">{notif.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CustomerDashboard;
